import type { Timestamp } from 'firebase/firestore';

export type {
  RolUsuario,
  MetodoAuth,
  EstadoPreRegistro,
  CustomClaims,
  MedicoPreRegistro,
  MedicoCompleto,
  EventoAuditoria,
  RegistroAuditoria,
} from './auth';

export type { Paciente } from './paciente';

export type {
  TipoAccionAuditoria,
  CategoriaAccion,
  RegistroAcceso,
} from './auditoria';
export { ACCION_CATEGORIA_MAP, CATEGORIA_LABELS } from './auditoria';

export type TipoSala = 'general' | 'pediatria' | 'urgencias' | 'especialidades' | 'psicologia' | 'ginecologia' | 'dermatologia' | 'otro';

export type PrioridadSala = 'normal' | 'alta' | 'urgente';

/** Operational state of a sala — controls whether new patients can enter the queue. */
export type EstadoSala = 'cerrada' | 'abierta' | 'pausa' | 'bloqueada' | 'emergencia';

/** Auto-assignment mode for the sala. */
export type ModoAsignacion = 'manual' | 'automatico' | 'hibrido';

/**
 * Simple time range used everywhere a `{inicio, fin}` is needed.
 * Renamed from the legacy `HorarioSala` — the latter is now the rich
 * scheduling entity below (subcollection `salas/{id}/horarios`).
 */
export interface BloqueHorario {
  inicio: string; // "HH:mm"
  fin: string;    // "HH:mm"
}

/** @deprecated Use {@link BloqueHorario} for `{inicio, fin}` ranges. */
export type HorarioSala = BloqueHorario;

// ── Estrategia de capacidad (M3 — nuevo modelo) ─────────────────────
//
// La capacidad efectiva de una sala se calcula como:
//   min(planificada(C), dinamica(A), override(D))
// donde:
//   C = suma de horarios de los MedicoSala asignados ese día
//   A = medicos online × ritmo en este instante
//   D = override manual del admin (capacidadOverridePorHora)
//
// `estrategiaCapacidad` permite degradar a un único modo cuando se
// quiere un comportamiento más simple.

export type EstrategiaCapacidad =
  | 'predictiva'   // solo C — planificada por horarios de médicos
  | 'dinamica'     // solo A — médicos online ahora × ritmo
  | 'hibrida'      // min(C, A) — recomendado para mayoría
  | 'manual';      // solo D — admin define cupos/hora explícitamente

/**
 * Regla de horario recurrente de una sala. Subcolección
 * `salas/{salaId}/horarios/{id}`. Una sala puede tener múltiples filas
 * (varios bloques por día, vigencias futuras, etc.).
 */
export interface ReglaHorarioSala {
  id: string;
  /** Día de la semana 0=Dom … 6=Sáb */
  diaSemana: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  horaInicio: string;        // "HH:mm"
  horaFin: string;           // "HH:mm"
  /** Fecha en que comienza a aplicar (inclusive) */
  vigenciaDesde: Timestamp;
  /** Fecha en que deja de aplicar (inclusive). null = indefinida */
  vigenciaHasta: Timestamp | null;
  /** Resolución de solapes — mayor gana */
  prioridad?: number;
  creadoPor?: string;
  creadoEn?: Timestamp;
}

/**
 * Suspensión puntual de una sala (feriado, capacitación, mantenimiento).
 * Subcolección `salas/{salaId}/suspensiones/{id}`.
 */
export interface SuspensionSala {
  id: string;
  tipo: 'dia_completo' | 'rango_horas' | 'multi_dia';
  /** Para tipo='dia_completo' o 'rango_horas' */
  fecha?: string;            // "YYYY-MM-DD" en hora Panamá
  /** Solo para 'rango_horas' */
  horaInicio?: string;
  horaFin?: string;
  /** Solo para 'multi_dia' */
  fechaDesde?: string;
  fechaHasta?: string;
  motivo: string;
  creadoPor: string;
  creadoEn: Timestamp;
}

/**
 * Ausencia de un médico que aplica a todas sus salas asignadas.
 * Colección `medicos/{medicoId}/ausencias/{id}`.
 */
export interface AusenciaMedico {
  id: string;
  tipo: 'vacaciones' | 'permiso' | 'enfermedad' | 'capacitacion' | 'otro';
  /** En zona Panamá. Para ausencia de día(s) completo(s) */
  fechaDesde: Timestamp;
  fechaHasta: Timestamp;
  /** Para ausencias parciales del día */
  horaInicio?: string;
  horaFin?: string;
  motivo: string;
  aprobadoPor?: string | null;
  creadoEn: Timestamp;
}

// ── M8: Turnos / Shifts como entidad de primera clase ────────────────────

/**
 * Skill / competencia del médico o requerimiento de la sala.
 * Permite skill-based routing (paciente↔médico por idioma, edad, sub-esp).
 */
export interface SkillsMedico {
  idiomas?: string[];                          // ['es', 'en', 'ngäbe']
  edadesAtendidas?: { min: number; max: number }; // ej. pediatría 0-14
  subEspecialidades?: string[];                // ej. ['cardiologia_infantil']
  nivelUrgenciaMax?: 1 | 2 | 3 | 4 | 5;        // máximo triage que puede manejar
  licencias?: Array<{
    jurisdiccion: string;                      // 'PA', 'CR', etc.
    validHasta: Timestamp;
  }>;
}

export interface SkillsRequeridosSala {
  edadesAtendidas?: { min: number; max: number };
  subEspecialidad?: string;
  idiomasMin?: string[];
  nivelUrgenciaMin?: 1 | 2 | 3 | 4 | 5;
}

/**
 * Política de routing por sala — define cómo se elige el siguiente médico.
 */
export interface RoutingPolicy {
  strategy: 'round_robin' | 'sticky_provider' | 'menos_ocupado' | 'fifo_prioridad';
  continuityWindowDays?: number;               // días hacia atrás para sticky-provider
  skillStrict?: boolean;                       // true=rechaza si mismatch, false=fallback
  maxConsultasPorMedicoDia?: number;
}

/**
 * Shift (Turno) — instancia concreta del horario de una sala para una fecha.
 * Generada nocturna por `generarShiftsDelDia` desde las reglas de horario.
 * Subcolección `salas/{salaId}/shifts/{shiftId}`.
 *
 * shiftId format: "YYYY-MM-DD_HH-mm_HH-mm" (ej. "2026-06-15_18-00_21-00")
 */
export interface Shift {
  id: string;
  fecha: string;                               // "YYYY-MM-DD"
  diaSemana: number;                           // 0-6
  horaInicio: string;                          // "HH:mm"
  horaFin: string;                             // "HH:mm"
  templateReglaId?: string;                    // ref a horarios/{regla} origen
  estado: 'planificado' | 'activo' | 'finalizado' | 'cancelado';
  capacidadFinal?: number;
  pacientesAtendidos: number;
  creadoEn: Timestamp;
  actualizadoEn: Timestamp;
}

/**
 * Asignación de un médico a un Shift específico. Permite múltiples médicos
 * por turno, rangos parciales (sólo de 18-19 del bloque 18-21) y sustituciones
 * (revocar + crear nueva con rol='sustituto').
 *
 * Subcolección `salas/{salaId}/shifts/{shiftId}/asignaciones/{aId}`.
 */
export interface ShiftAssignment {
  id: string;
  medicoId: string;
  rol: 'titular' | 'sustituto' | 'reemplazo_break';
  /** Rango parcial dentro del Shift. null = todo el shift. */
  rangoHorario?: { inicio: string; fin: string };
  /** Si rol != 'titular', motivo de la asignación. */
  motivoSiSustituto?: string;
  /** Referencia a la asignación que esta sustituye (si aplica). */
  reemplazaA?: string;
  asignadoPor: string;
  asignadoEn: Timestamp;
  /** Si false, la asignación fue revocada. Mantenemos el doc para audit. */
  vigente: boolean;
  revocadaEn?: Timestamp;
  revocadaPor?: string;
  revocadaMotivo?: string;
}

/**
 * Estado granular del médico (M8 — incluye charting y break_almuerzo).
 */
export type EstadoMedicoV2 =
  | 'offline'
  | 'disponible'
  | 'en_consulta'
  | 'charting'             // post-consulta, completando notas
  | 'pausa'                // break personal corto 5-15 min
  | 'break_almuerzo'       // break programado 30-60 min
  | 'buffer';              // transición automática post-consulta

/**
 * Histórico append-only de presencia del médico.
 * Colección `medicos/{medicoId}/presence/{pId}`.
 */
export interface PractitionerPresence {
  id: string;
  desde: Timestamp;
  hasta: Timestamp | null;                     // null = activo
  estado: EstadoMedicoV2;
  shiftId?: string;
  motivoCambio?: string;
}

/**
 * Slot de atención materializado para un día específico.
 * Subcolección `salas/{salaId}/slots/{YYYY-MM-DD_HH-mm}`.
 *
 * Generado por `regenerarSlotsDia` al abrir el día, actualizado
 * runtime cuando cambian médicos online u override.
 */
export interface SlotCalculado {
  id: string;                       // "2026-05-16_08-00"
  fecha: string;                    // "YYYY-MM-DD"
  horaInicio: string;               // "HH:mm"
  horaFin: string;                  // "HH:mm"
  capacidadPlanificada: number;     // C
  capacidadDinamica: number;        // A (snapshot)
  capacidadOverride: number | null; // D
  capacidadEfectiva: number;        // min(C, A, D)
  cuposOcupados: number;
  cuposDisponibles: number;
  estado: 'abierto' | 'cerrado' | 'en_riesgo' | 'suspendido' | 'lleno';
  medicosAsignados: string[];
  medicosOnline: string[];
  motivoSuspension?: string;
  actualizadoEn: Timestamp;
}

/** Scheduled or manual pause window for the sala (e.g. lunch break). */
export interface PausaSala {
  /** Reason / label (e.g. "Almuerzo", "Capacitación") */
  motivo?: string;
  /** Start time in "HH:mm" (used for recurring pauses) */
  inicio?: string;
  /** End time in "HH:mm" */
  fin?: string;
  /** Whether this pause auto-resumes when `fin` is reached */
  autoResume?: boolean;
  /** Once-only pauses use these absolute timestamps */
  inicioEn?: Timestamp;
  finEn?: Timestamp;
}

/** Dynamic capacity snapshot computed by `calcularCapacidadDinamica`. */
export interface CapacidadDinamica {
  /** Patients waiting in queue right now */
  pacientesEnEspera: number;
  /** Active doctors available in the sala */
  medicosActivos: number;
  /** Available slots (negative means over capacity) */
  cuposDisponibles: number;
  /** Estimated wait time in minutes */
  estimadoEsperaMin: number;
  /** Snapshot of the active franja horaria */
  franjaActual?: {
    horaInicio: string;
    horaFin: string;
    cupos: number;
    cuposOcupados: number;
  };
}

export interface Sala {
  id: string;
  nombre: string;
  descripcion: string;
  icono?: string;
  tipo?: TipoSala;
  activa: boolean;
  pacientesEnEspera: number;

  // ── Vigencia general de la sala (M3 v2) ─────────────────────────
  /** Fecha desde la que la sala empieza a operar (inclusive). */
  vigenciaDesde?: Timestamp;
  /** Fecha en la que la sala deja de operar (inclusive). null = indefinida. */
  vigenciaHasta?: Timestamp | null;

  // ── Estrategia de capacidad (M3 v2) ─────────────────────────────
  estrategiaCapacidad?: EstrategiaCapacidad;
  /** Override manual del admin (D). null o undefined = sin override. */
  capacidadOverridePorHora?: number | null;
  permiteOverbooking?: boolean;
  factorOverbooking?: number;          // ej. 1.15 = 15% extra
  throttleUltimaHora?: boolean;

  // ── Bandera de migración M3 v2 ──────────────────────────────────
  /**
   * Si true, la sala usa el nuevo modelo de horarios (subcolección
   * `horarios` + `suspensiones`) y NO los campos legacy `horario`,
   * `diasAtencion`, `pausas` directamente. Mientras esté false, las
   * CFs caen al comportamiento legacy.
   */
  usaHorariosV2?: boolean;

  // ── Configuración legacy (M3 v1) — mantener mientras la migración esté en curso ──
  /** @deprecated Usar subcolección `horarios` cuando usaHorariosV2 = true */
  horario?: BloqueHorario;
  /** @deprecated Usar subcolección `horarios` cuando usaHorariosV2 = true */
  diasAtencion?: number[];          // 0=Dom, 1=Lun ... 6=Sab
  limitePacientes?: number;         // max patients in queue (cap absoluto cola)
  tiempoEstimadoMin?: number;       // estimated minutes per consultation
  tiempoConsultaMin?: number;       // alias used in some Cloud Functions
  /** Default per-consulta time. Sobrescribible en MedicoSala. */
  tiempoConsultaDefault?: number;
  /** Minutos EXTRA sobre el tiempo de consulta = duración total de la sesión
   *  de video (margen para cierre/despedida). Default 5. */
  bufferSesionMin?: number;
  /** Minutos que tiene el médico para INICIAR la videollamada tras tomar al
   *  paciente; si no la inicia, el paciente vuelve a la cola. Default 5. */
  timeoutPreVideoMin?: number;
  /** Segundos de gracia si el médico pierde conexión durante una llamada
   *  activa, antes de devolver el paciente a la cola por fallo técnico.
   *  Default 120. */
  graciaReconexionMedicoSeg?: number;
  /** Minutos para redactar y firmar la nota tras colgar la videollamada. */
  graciaFinalizacionMin?: number;
  prioridad?: PrioridadSala;
  medicoAsignado?: string;          // doctor name (legacy single doctor)
  medicoAsignadoId?: string;
  requiereAprobacion?: boolean;
  instrucciones?: string;

  // Operational state (managed by Cloud Functions)
  estado?: EstadoSala;
  modoAsignacion?: ModoAsignacion;
  /** COLA-02: en modo Híbrido, ofrecer al médico (aceptar/rechazar) en vez de
   *  asignar directo. Opt-in por sala (default false). */
  ofertaHibridaActiva?: boolean;
  /** COLA-02: segundos que dura una oferta antes de expirar (default 45). */
  ofertaHibridaTtlSegundos?: number;
  /** CAP: horizonte en horas para la capacidad dinámica (default 3). */
  horizonteCapacidadHoras?: number;
  /** CAP: si true, el gate de admisión usa la capacidad DINÁMICA (médicos en
   *  turno) en vez del límite fijo. Opt-in por sala; el cron mantiene los
   *  campos que lee el gate. Default false (gate estático). */
  gateDinamico?: boolean;
  /** CAP: en modo dinámico, si true sólo cuentan para la capacidad los médicos
   *  con señal reciente (ver `ventanaPresenciaMin`); si false (default) cuenta
   *  todos los médicos de la sala (roster) — más estable. */
  descontarDesconectados?: boolean;
  /** CAP: minutos sin señal tras los que un médico deja de contar para la
   *  capacidad (sólo si `descontarDesconectados`). Recortado a 1–120. Default 10. */
  ventanaPresenciaMin?: number;
  /** Plantillas personalizadas de los mensajes de rechazo que ve el paciente.
   *  Claves opcionales; las ausentes/vacías usan el texto por defecto.
   *  Ver `src/config/mensajesPaciente.ts`. */
  mensajesPaciente?: Partial<Record<import('../config/mensajesPaciente').MensajeKey, string>>;
  /** Cita en HCIS (ConfirmarCitaHcis): códigos del catálogo de HCIS. `seisCentroId`
   *  es codigoCentroMedico (numérico), `seisServicioId` codigoServicio y
   *  `seisPrestacionId` codPrestacion (p. ej. CO_PRVISUB). */
  seisServicioId?: string;
  seisCentroId?: string;
  seisPrestacionId?: string;
  /** Cita SEIS: intervalo en minutos al que se redondea HACIA ARRIBA la hora
   *  de inicio de la teleconsulta para pedir el cupo (ej. 8:08 con 10 → 8:10;
   *  8:51 con 10 → 9:00). Recortado a 1–60. Default 10. */
  seisRedondeoMinutos?: number;
  // CAP: campos calculados por el cron reconciliarCuposEnVivo (solo lectura).
  medicosEnTurno?: number;
  medicosLibresAhora?: number;
  capacidadDinamica?: number;
  cupoDinamicoDisponible?: number;
  capacidadAprox?: number;
  cupoDisponibleAprox?: number;
  motivoSinCupo?: string;
  algoritmoAsignacion?: 'round_robin' | 'menos_ocupado' | 'prioridad' | 'fifo_prioridad';
  bufferCierreMins?: number;        // minutes before close to stop accepting patients
  bufferEntreConsultasSeg?: number; // seconds between consultations (per doctor)
  timeoutNoShowSeg?: number;        // seconds before marking a consulta no-show
  maxPacientesPorMedicoDia?: number | null;
  permitirRechazo?: boolean;
  permitirTransferencias?: boolean;
  permitirSeleccionManual?: boolean;
  medicosActivos?: string[];        // uids of doctors currently active in the sala
  /** @deprecated Usar subcolección `suspensiones` cuando usaHorariosV2 = true */
  pausas?: PausaSala[];             // scheduled pauses (recurring lunches)
  pausaActual?: PausaSala | null;
  capacidad?: CapacidadDinamica;    // optional cached capacity snapshot
  pacientesAtendidosHoy?: number;   // reset diario por cron

  // ── M8: skill matching + routing policy ─────────────────────────
  skillsRequeridos?: SkillsRequeridosSala;
  routingPolicy?: RoutingPolicy;
  /** Modo surge (contingencia) — aumenta capacidad temporalmente. */
  surgeMode?: {
    activo: boolean;
    factorCapacidad: number;   // ej. 1.5 = +50%
    permitirOverbooking: boolean;
  };

  creadoPor?: string;
  creadoEn?: Timestamp;
  actualizadoEn?: Timestamp;

  /**
   * `eliminarSala` es un soft-delete: no borra el documento (preserva
   * médicos/horarios para auditoría), solo pone `activa:false` y marca estos
   * campos. Distinto de `activa:false` por el toggle manual del admin (esa
   * sala sigue en la lista, reactivable) — una sala con `eliminadaEn` debe
   * ocultarse de la lista por defecto.
   */
  eliminadaEn?: Timestamp;
  eliminadaPor?: string;
}

/** Per-doctor configuration within a sala (subcollection `salas/{id}/medicos_sala`). */
export interface MedicoSala {
  medicoId: string;
  habilitado: boolean;

  // ── Vigencia de la asignación (M3 v2) ───────────────────────────
  vigenciaDesde?: Timestamp;
  vigenciaHasta?: Timestamp | null;

  // ── Rol y carga ─────────────────────────────────────────────────
  rolEnSala?: 'titular' | 'suplente';
  peso?: number;                    // weight for round-robin (higher = more)
  /** Per-doctor override of Sala.tiempoConsultaDefault. */
  tiempoConsultaCustom?: number | null;
  /** Si true, este médico usa el horario de la sala. Si false, usa la
   *  sub-sub-colección `medicos_sala/{id}/horarios` propia. */
  usaHorarioSala?: boolean;
  /** Permite conectarse fuera de su horario configurado (agrega capacidad ad-hoc). */
  permiteHoraExtra?: boolean;
  maxPacientesDia?: number | null;

  /** @deprecated Si usaHorarioSala === false, usar subcolección `horarios` */
  horarioPersonalizado?: BloqueHorario;

  // ── Estado runtime ──────────────────────────────────────────────
  pausaHasta?: Timestamp;
  estado?: 'disponible' | 'en_consulta' | 'pausa' | 'buffer' | 'offline';
  consultaActual?: string | null;
  conectadoEn?: Timestamp | null;
  pacientesAtendidosHoy?: number;   // reset diario
  tiempoPromedioConsultaMin?: number;

  asignadoPor?: string;
  agregadoEn?: Timestamp;
  actualizadoEn?: Timestamp;
}

/** Time slot in `salas/{id}/franjas_horarias` (doc id "HHMM"). */
export interface FranjaHoraria {
  horaInicio: string;
  horaFin: string;
  cupos: number;
  cuposOcupados: number;
  estado: 'disponible' | 'llena' | 'cerrada';
}

export type DisposicionConsulta =
  | 'completada'
  | 'paciente_no_presento'
  | 'paciente_rechazo'
  | 'fallo_tecnico'
  | 'cancelada_medico'
  | 'cancelada_supervisor'
  | 'cancelada_paciente'
  | 'incompleta'
  | 'referida'
  // Auto-cierre del cron cerrarSesionesExpiradas cuando se agota el tiempo
  // máximo de la sesión con el video activo (tareasScheduled.ts).
  | 'tiempo_excedido';

/** Etiquetas legibles del cierre de la consulta (historial, detalle, badges). */
export const DISPOSICION_LABELS: Record<DisposicionConsulta, string> = {
  completada: 'Atendida',
  paciente_no_presento: 'Paciente no contestó',
  paciente_rechazo: 'Paciente rechazó la llamada',
  fallo_tecnico: 'Falla técnica',
  cancelada_medico: 'Cancelada por el médico',
  cancelada_supervisor: 'Cancelada por supervisor',
  cancelada_paciente: 'Cancelada por el paciente',
  incompleta: 'Incompleta',
  referida: 'Referida',
  tiempo_excedido: 'Tiempo de sesión agotado',
};

/** Descripción en lenguaje claro de cada cierre — qué pasó exactamente con la
 *  consulta. Se muestra junto a la etiqueta en el detalle del historial para
 *  que cualquier usuario entienda el motivo sin conocer los códigos internos. */
export const DISPOSICION_DESCRIPCIONES: Record<DisposicionConsulta, string> = {
  completada: 'La videoconsulta se realizó y el médico completó la atención.',
  paciente_no_presento: 'El paciente nunca entró a la videollamada y la consulta se cerró por inasistencia.',
  paciente_rechazo: 'El paciente rechazó la videollamada cuando el médico intentó iniciarla.',
  fallo_tecnico: 'La consulta no pudo completarse por problemas técnicos (conexión, audio o video).',
  cancelada_medico: 'El médico canceló la consulta antes de completar la atención.',
  cancelada_supervisor: 'Un supervisor canceló la consulta desde el portal (p. ej. al cerrar la cola del día).',
  cancelada_paciente: 'El paciente canceló su solicitud o abandonó la fila antes de ser atendido.',
  incompleta: 'La consulta se cerró sin completar la atención o la nota clínica.',
  referida: 'El paciente fue atendido y referido a un especialista u otro nivel de atención.',
  tiempo_excedido: 'El sistema cerró la consulta automáticamente al agotarse el tiempo máximo de la sesión.',
};

/** Lifecycle states for a consulta. */
export type EstadoConsulta =
  | 'en_espera'    // patient waiting in queue
  | 'asignada'     // auto-assigned to a doctor in hybrid mode (awaiting accept/reject)
  | 'en_atencion'  // doctor accepted, consultation in progress
  | 'finalizada'   // consultation closed
  | 'no_show'      // patient didn't show up within timeout
  | 'transferida'  // moved to another sala
  | 'cancelada';   // cancelled by patient or doctor

export type PrioridadConsulta = 'baja' | 'normal' | 'media' | 'alta';

export interface Consulta {
  id: string;
  pacienteNombre: string;
  pacienteId: string;
  /** Firebase UID del paciente (móvil). Distinto de pacienteId, que puede ser la cédula. */
  pacienteUid?: string;
  pacienteEdad?: number;
  pacienteSexo?: string;
  /** Foto de perfil del paciente (denormalizada desde users/{uid}.fotoURL). */
  pacienteFotoURL?: string | null;
  /** Posición GPS del paciente al SOLICITAR la consulta (best-effort de la
   *  app móvil; ausente si negó el permiso o el GPS falló). Auxiliar para
   *  emergencias y para contrastar con la región declarada del perfil. */
  pacienteUbicacion?: {
    lat: number;
    lng: number;
    /** Precisión reportada por el GPS, en metros (null si no se conoce). */
    precisionM: number | null;
    capturadaEn?: Timestamp;
  } | null;
  motivoConsulta: string;
  estado: EstadoConsulta;
  salaId: string;
  salaNombre: string;
  medicoId?: string;
  medicoNombre?: string;
  /** Foto de perfil del médico (denormalizada desde medicos/{uid}.avatarUrl). */
  medicoFotoURL?: string | null;
  /** Position in queue (1-indexed), maintained by Cloud Functions. */
  posicion?: number;
  /** Backwards-compat alias for posicion */
  posicionEnCola?: number;
  prioridad?: PrioridadConsulta;
  origen?: 'app_movil' | 'portal_web' | 'admin';
  creadoEn: Timestamp;
  /** When auto-assignment happened (hybrid/automático mode). */
  asignadaEn?: Timestamp;
  /** Deadline for the doctor to accept the assignment before reassign. */
  asignacionExpiraEn?: Timestamp;
  iniciadaEn?: Timestamp;
  finalizadaEn?: Timestamp;
  /** Transfer trail */
  salaIdOrigen?: string;
  transferidaEn?: Timestamp;
  notas?: string;
  notasActualizadasEn?: Timestamp;
  disposicion?: DisposicionConsulta;
  /** UID de quien canceló (paciente cuando canceló su propia solicitud en cola). */
  canceladaPor?: string;
  requiereSeguimiento?: boolean;
  observacionesCierre?: string;
  duracionSegundos?: number;
  /** Calidad de la llamada autorreportada (legado) — NO es la calificación del paciente, ver `calificacion`. */
  calidadLlamada?: 1 | 2 | 3 | 4 | 5;
  /** Calificación del PACIENTE a la consulta finalizada (portal_calificarConsulta). */
  calificacion?: {
    estrellas: 1 | 2 | 3 | 4 | 5;
    comentario?: string;
    calificadoEn?: Timestamp;
    calificadoPorUid?: string;
  };
  // Médicos que devolvieron esta consulta a la cola (devolverConsultaACola):
  // excluidos de la auto-asignación / auto-aceptación de ESTA consulta.
  devueltaPor?: string[];
  devueltaACola?: boolean;
  // LiveKit presence — set exclusively by livekitWebhook Cloud Function
  medicoEnRoom?: boolean;
  /**
   * Resultado del aviso de videollamada al paciente, escrito por el backend
   * (`pushTeleconsulta.avisarLlamadaEntrante`). Permite que el portal muestre
   * si el paciente REALMENTE fue avisado en vez de un "Llamando…" genérico.
   */
  avisoLlamada?: {
    en?: Timestamp;
    estado?: 'enviando' | 'entregado' | 'fallido' | 'sin_dispositivos' | 'sin_app';
    dispositivos?: number;
    aceptados?: number;
    origen?: 'token_medico' | 'webhook' | 'reintento';
  };
  medicoJoinedAt?: Timestamp;
  medicoLeftAt?: Timestamp;
  pacienteEnRoom?: boolean;
  pacienteJoinedAt?: Timestamp;
  // Reintentos de "timbrar" al paciente (portal_reintentarLlamadaPaciente)
  llamadaIntentos?: number;
  ultimoIntentoLlamadaEn?: Timestamp;
  // El paciente rechazó la llamada entrante (portal_rechazarLlamadaPaciente);
  // se limpia al reintentar.
  llamadaRechazada?: boolean;
  llamadaRechazadaEn?: Timestamp;
  rechazosLlamada?: number;
  // M8 — primer corte del doble corte: video terminado pero consulta sigue
  // en_atencion mientras el médico edita la nota. El paciente queda liberado.
  videoFinalizado?: boolean;
  videoFinalizadoEn?: Timestamp;
  // ── Ciclo de tiempo de la sesión de teleconsulta ──
  /** Deadline para que el médico INICIE la videollamada tras tomar al paciente
   *  (fase pre-video). Al iniciar el video se borra. */
  preVideoExpiraEn?: Timestamp;
  /** Momento en que el video realmente inició (primer join del médico al room).
   *  Distingue "nunca inició" de "inició y el médico se cayó". */
  videoInicioEn?: Timestamp;
  /** Duración total de la sesión de video en minutos (tiempoConsulta + buffer). */
  sessionMaxMin?: number;
  /** Deadline de la sesión de video; arranca al iniciar el video. */
  sessionEndsAt?: Timestamp;
  /** Marca de que el sistema cerró la consulta automáticamente. */
  finalizadaPorSistema?: boolean;
  /** Motivo del cierre automático (p. ej. 'tiempo_excedido'). */
  motivoCierre?: string;
  /** Estado de la creación de la cita en SEIS (al tomar al paciente). */
  citaSeis?: CitaSeisEstado;
}

export interface CitaSeisEstado {
  estado: 'enviada' | 'error' | 'no_enviada_deshabilitada';
  /** Resultado del API de HCIS (OK, HCIS_RECHAZO, HCIS_SIN_RESPUESTA…) o del
   *  propio portal (CONFIGURACION_INCOMPLETA, TIEMPO_AGOTADO…). */
  codigo?: string;
  /** true si no se sabe si HCIS registró la cita; el reintento reenvía el mismo cupo. */
  incierto?: boolean;
  /** true si el fallo es de configuración o de integración y lo debe ver soporte. */
  requiereSoporte?: boolean;
  /** Cupo pedido, hora de Panamá: "2026-11-05T07:00:00". */
  fechaCitaInicio?: string;
  fechaCitaFin?: string;
  /** Fecha solicitada (DD-MM-YYYY) y hora (HH:mm) del cupo, para mostrar. */
  fechaSolicitada?: string;
  horaSolicitada?: string;
  enviadaEn?: Timestamp;
  actualizadaEn?: Timestamp;
  /** Respuesta cruda del gateway (éxito o rechazo). */
  respuesta?: unknown;
  /** Mensaje textual de HCIS cuando la cita SÍ se registró. */
  mensaje?: string;
  /** Mensaje de error (cuando estado === 'error'); si el rechazo vino de
   *  HCIS incluye su `mensaje` literal (p. ej. "cupo no determinado…"). */
  error?: string | null;
  /** true cuando HCIS rechazó específicamente por falta de cupo. */
  sinCupo?: boolean;
  /** Motivo cuando no se envió (deshabilitado). */
  motivo?: string;
  intentos?: number;
}

export interface Medico {
  id: string;
  nombre: string;
  especialidad: string;
  estado: 'disponible' | 'en_consulta' | 'no_disponible';
  salaActiva?: string;
  avatarUrl?: string;
}
